import { useState, useEffect, useCallback } from 'react';
import { authedApiFetch } from 'utils/Api';

const useSalesforceReportAnalysis = (reportId) => {
	const [suggestedRules, setSuggestedRules] = useState([]);
	const [loading, setLoading] = useState(Boolean(reportId));
	const [error, setError] = useState(null);

	const analyzeReport = useCallback(
		async ({ signal } = {}) => {
			if (!reportId) {
				setSuggestedRules([]);
				setLoading(false);
				return;
			}

			setLoading(true);
			setError(null);

			try {
				const data = await authedApiFetch({
					endpoint: '/input_signal_rules/generate',
					method: 'POST',
					params: {
						report_id: reportId,
					},
					signal,
				});

				// Ignore responses from requests that were aborted during unmount
				if (signal?.aborted) return;

				const result = Array.isArray(data)
					? data
					: data?.recommended_rules || [];

				setSuggestedRules(result);
			} catch (err) {
				if (err.name !== 'AbortError') {
					setError(err.message || 'Failed to analyze Salesforce report');
				}
			} finally {
				if (!signal?.aborted) {
					setLoading(false);
				}
			}
		},
		[reportId]
	);

	useEffect(() => {
		// Abort any in-flight requests if the component unmounts.
		// In development, React Strict Mode mounts components twice, which can trigger
		// duplicate LLM requests and allow stale responses to overwrite newer ones.
		// This is noticeable in the UI because the generated recommendations are not always returned
		// in the same order.
		const controller = new AbortController();

		analyzeReport({ signal: controller.signal });

		return () => {
			controller.abort();
		};
	}, [analyzeReport]);

	return {
		suggestedRules,
		loading,
		error,
	};
};

export default useSalesforceReportAnalysis;